/**
 * Email connect-account hooks.
 *
 * Manages connected external mailboxes (OAuth Gmail/Microsoft + generic
 * IMAP/POP3). Mirrors the calendar arche's useSyncConnections so the OAuth
 * popup + BroadcastChannel handshake behaves identically.
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type EmailConnection, type EmailImapConnectInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Channel the email OAuth callback page broadcasts on when a connection lands. */
export const EMAIL_OAUTH_CHANNEL = 'fias-email-connections-oauth';

const conn = queryKeys.email.connections;

/** Fetch the user's connected mailboxes; refreshes when the OAuth popup completes. */
export function useEmailConnections() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = new BroadcastChannel(EMAIL_OAUTH_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data?.type === 'oauth-complete') {
        queryClient.invalidateQueries({ queryKey: conn.all });
      }
    };
    return () => channel.close();
  }, [queryClient]);

  return useQuery<EmailConnection[]>({
    queryKey: conn.list(),
    queryFn: async () => {
      const res = await api.listEmailConnections();
      return res.accounts;
    },
  });
}

/** Start OAuth for Gmail/Microsoft — opens the provider authorization URL. */
export function useInitEmailOAuth() {
  return useMutation({
    mutationFn: async (provider: 'gmail' | 'microsoft') => {
      return api.initEmailConnectionOAuth(provider);
    },
    onSuccess: (data) => {
      window.open(data.authorizationUrl, '_blank', 'noopener,noreferrer');
    },
  });
}

/** Connect a generic IMAP/POP3 + SMTP mailbox (password / app-password). */
export function useConnectEmailImap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: EmailImapConnectInput) => api.connectEmailImap(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

/** Disconnect (revoke + soft-delete) a connected mailbox. */
export function useDisconnectEmailConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string) => api.disconnectEmailConnection(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

/**
 * Reorder the connected mailboxes. Optimistic — the drag should feel instant, so
 * we reorder the cached list immediately and roll back if the server rejects it.
 */
export function useReorderEmailConnections() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (order: string[]) => api.reorderEmailConnections(order),
    onMutate: async (order: string[]) => {
      await queryClient.cancelQueries({ queryKey: conn.list() });
      const previous = queryClient.getQueryData<EmailConnection[]>(conn.list());
      if (previous) {
        const byId = new Map(previous.map((a) => [a.accountId, a]));
        // Reorder by the given ids; append any not named (defensive) in prior order.
        const reordered = [
          ...order.map((id) => byId.get(id)).filter((a): a is EmailConnection => !!a),
          ...previous.filter((a) => !order.includes(a.accountId)),
        ];
        queryClient.setQueryData<EmailConnection[]>(conn.list(), reordered);
      }
      return { previous };
    },
    onError: (_err, _order, context) => {
      if (context?.previous) queryClient.setQueryData(conn.list(), context.previous);
    },
    onSuccess: (data) => {
      // Adopt the server's authoritative order (with refreshed sortOrder values)
      // so every consumer of this shared cache — the mailbox account pulldown,
      // the unified sidebar, the compose "From" list — shows the same order.
      queryClient.setQueryData<EmailConnection[]>(conn.list(), data.accounts);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

/** Edit an account's display name and/or make it primary. */
export function useUpdateEmailConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      accountId: string;
      input: Parameters<typeof api.updateEmailConnection>[1];
    }) => api.updateEmailConnection(vars.accountId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

/** Edit a generic account's IMAP/POP3/SMTP transport (server re-probes first). */
export function useUpdateEmailTransport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      accountId: string;
      input: Parameters<typeof api.updateEmailConnectionTransport>[1];
    }) => api.updateEmailConnectionTransport(vars.accountId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}

/** Re-enter a generic account's credentials (server re-probes first). */
export function useUpdateEmailCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      accountId: string;
      input: Parameters<typeof api.updateEmailConnectionCredentials>[1];
    }) => api.updateEmailConnectionCredentials(vars.accountId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conn.all });
    },
  });
}
